import { Checkbox, Image, Responsive, UIHeading4, UIText1, UIText2 } from '@cian/ui-kit';
import { Document16, Photo16 } from '@cian/ui-kit/prev';
import classnames from 'classnames';
import * as React from 'react';

import * as styles from './ChatListItem.css';
import { ChatListItemSwipeButton, EChatListItemSwipeButtonType } from './ChatListItemSwipeButton';
// @ts-expect-error TODO: исправить импорт картинок
import advertImg from './assets/advert.svg';
import { TFormattedCompanionData } from '../../hooks';
import { EMessageFileTypes } from '../../types/messageFiles';
import { TOutcomingStatus } from '../../types/messages';
import { ERoute, TRoute } from '../../types/route';
import { formatShortDate } from '../../utils/formatShortDate';
import { ImageNone } from '../ImageNone';
import { MessageStatusIcon } from '../MessageStatusIcon';

type TChatListItemProps = {
  chatId: string;
  checkboxChecked: boolean;
  checkboxShown: boolean;
  companion: TFormattedCompanionData;
  isActive: boolean;
  fileLastMessage?: { type: EMessageFileTypes; name: string };
  isOfferLastMessage?: boolean;
  message: string;
  isMultilineMessage: boolean;
  onClick(): void;
  onSwipeButtonClick(action: EChatListItemSwipeButtonType): void;
  status?: TOutcomingStatus;
  unreadCount: number;
  updated: number;
  previewImage?: string;
  offerInfo?: string;
  currentRoute: TRoute;
  previewAvatarNode: React.ReactNode | null;
  lastMessageName?: string;
};

const CHAT_LIST_FILE_ICON = {
  [EMessageFileTypes.Image]: <Photo16 />,
  [EMessageFileTypes.ImageAsDocument]: <Document16 />,
  [EMessageFileTypes.Document]: <Document16 />,
};

export const ChatListItem: React.VFC<TChatListItemProps> = ({
  chatId,
  checkboxChecked,
  checkboxShown,
  companion = {},
  isActive,
  fileLastMessage,
  message,
  isMultilineMessage,
  isOfferLastMessage,
  onClick,
  onSwipeButtonClick,
  status,
  unreadCount,
  updated,
  previewImage,
  offerInfo,
  currentRoute,
  previewAvatarNode,
  lastMessageName,
}) => {
  const [isButtonShown, setButtonShown] = React.useState(false);
  const [date, setDate] = React.useState('');

  const { companionName, accountTypeName, companionIdStr } = companion;

  // Кнопка слева не должна быть видна, пока не смахнули вправо. К сожалению, только JS...
  React.useEffect(() => {
    setButtonShown(false);
    setTimeout(() => setButtonShown(true), 0);
  }, [currentRoute]);

  React.useEffect(() => setDate(formatShortDate(updated)), [updated]);

  return (
    <div
      className={classnames(styles['item'], isActive && styles['item--active'])}
      onClick={onClick}
      data-chatid={chatId}
    >
      {!checkboxShown && (
        <Responsive phone tablet>
          <div
            className={classnames(
              styles['left-swipe-button'],
              /* TODO: функционал непрочитанности пока не готов на сервере, а прочитать можно */
              unreadCount > 0 && isButtonShown && styles['left-swipe-button--shown'],
            )}
          >
            <ChatListItemSwipeButton
              type={unreadCount > 0 ? EChatListItemSwipeButtonType.Read : EChatListItemSwipeButtonType.Unread}
              onClick={onSwipeButtonClick}
            />
          </div>
        </Responsive>
      )}

      <div className={styles['item-container']}>
        {checkboxShown && (
          <span className={styles['checkbox']}>
            <Checkbox onClick={e => e.preventDefault()} value={chatId} checked={checkboxChecked} />
          </span>
        )}
        <div className={styles['preview']}>
          {previewImage ? (
            <Image width={60} height={60} borderRadius="4px" objectFit="cover" src={previewImage} />
          ) : (
            <ImageNone type={offerInfo ? 'offer' : 'agent'} />
          )}
          {previewAvatarNode && <div className={styles['preview_overlay']} />}
          {previewAvatarNode && <div className={styles['preview_avatar']}>{previewAvatarNode}</div>}
        </div>
        <div className={styles['info']}>
          <div className={styles['common']}>
            <span className={styles['username']}>
              <UIHeading4 whiteSpace="nowrap">
                {companionName || (
                  <>
                    {accountTypeName}&nbsp;<UIText2 color="gray60_100">{`(${companionIdStr})`}</UIText2>
                  </>
                )}
              </UIHeading4>
            </span>
            <UIText2 color="gray60_100" whiteSpace="nowrap">
              {date}
            </UIText2>
            {status && <MessageStatusIcon status={status} />}
          </div>
          <div className={styles['details']}>
            <div className={styles['texts']}>
              {offerInfo && (
                <UIText1>
                  <div className={styles['offer-info']}>{offerInfo}</div>
                </UIText1>
              )}
              <UIText1 color="gray60_100">
                <span className={styles['message']}>
                  {lastMessageName && <span>{lastMessageName}</span>}
                  {fileLastMessage && (
                    <>
                      {fileLastMessage.type
                        ? CHAT_LIST_FILE_ICON[fileLastMessage.type]
                        : CHAT_LIST_FILE_ICON[EMessageFileTypes.Document]}
                      <span className={styles['text']}>
                        {fileLastMessage.type === EMessageFileTypes.Image ? 'Фото' : fileLastMessage.name}
                      </span>
                    </>
                  )}
                  {isOfferLastMessage && (
                    <>
                      <img src={advertImg} width={16} height={16} />
                      <span className={styles['text']}>Объявление</span>
                    </>
                  )}
                  {!fileLastMessage && !isOfferLastMessage && (
                    <span className={classnames(styles['text'], isMultilineMessage && styles['multiline'])}>
                      {message}
                    </span>
                  )}
                </span>
              </UIText1>
            </div>
            {unreadCount !== 0 && (
              <span data-testid="unreadCounter" className={styles['counter']}>
                {unreadCount}
              </span>
            )}
          </div>
        </div>
      </div>
      {!checkboxShown && (
        <Responsive phone tablet>
          {currentRoute === ERoute.Spam ? (
            <ChatListItemSwipeButton type={EChatListItemSwipeButtonType.Unblock} onClick={onSwipeButtonClick} />
          ) : (
            <>
              <ChatListItemSwipeButton type={EChatListItemSwipeButtonType.Delete} onClick={onSwipeButtonClick} />
              <ChatListItemSwipeButton
                type={
                  currentRoute === ERoute.Archive
                    ? EChatListItemSwipeButtonType.Return
                    : EChatListItemSwipeButtonType.Archive
                }
                onClick={onSwipeButtonClick}
              />
            </>
          )}
        </Responsive>
      )}
    </div>
  );
};
